'use client';

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import DatePicker from 'react-datepicker';

import { HotelsClient, type HotelSuggestion, type Suggestions } from '@wl/api-client';
import OccupancyPicker, { type Room } from './OccupancyPicker';
import {
  buildDefaultDates, buildQueryString, serializeOccupancyCMS, parseOccupancyCMS,
  summarizeOccupancy, nightsBetween, bumpPastDates, HOTELS_API_BASE_URL, PROXY_PREFIX,
  SUGGESTION_ENGINE_DEFAULT, resolveSuggestionEngine, type SuggestionEngine,
  parseBipMaskLength,
} from '../config';
import { addRecentSearch } from '../_hooks/useRecentSearches';

// ── Suggestion type config ────────────────────────────────────────────────────

const TYPE_CONFIG: Record<string, { icon: string; cls: string }> = {
  city:      { icon: 'fa-city',           cls: 'city'    },
  hotel:     { icon: 'fa-hotel',          cls: 'hotel'   },
  resort:    { icon: 'fa-umbrella-beach', cls: 'resort'  },
  villa:     { icon: 'fa-home',           cls: 'villa'   },
  apartment: { icon: 'fa-home',           cls: 'villa'   },
  airport:   { icon: 'fa-plane',          cls: 'airport' },
  region:    { icon: 'fa-map-marked-alt', cls: 'region'  },
};

// ── Elasticsearch grouped-dropdown helpers (parallel engine only) ──────────────
// The ES suggestion dropdown is a DIFFERENT structure from the Mongo flat list: it groups results
// into a "CITIES" section (top 3) and a "HOTELS" section (top 5), with the matched substring
// highlighted. Kept intact from the BCP-1218 design; rendered only when the ES engine is active.

function highlightMatch(text: string, query: string) {
  if (!query || !text) return text;
  const index = text.toLowerCase().indexOf(query.toLowerCase());
  if (index === -1) return text;
  return (
    <>
      {text.slice(0, index)}
      <span className="sb-sug-hl">{text.slice(index, index + query.length)}</span>
      {text.slice(index + query.length)}
    </>
  );
}

interface SuggestionRowProps {
  cfg: { icon: string; cls: string };
  title: string;
  subtitle?: string;
  query: string;
  onClick?: () => void;
}

function SuggestionRow({ cfg, title, subtitle, query, onClick }: SuggestionRowProps) {
  return (
    <button type="button" role="option" className="sb-sug-item" onClick={onClick}>
      <div className={`sb-sug-icon sb-sug-icon--${cfg.cls}`} aria-hidden="true">
        <i className={`fas ${cfg.icon}`} />
      </div>
      <div className="sb-sug-text">
        <div className="sb-sug-name">{highlightMatch(title, query)}</div>
        {subtitle && <div className="sb-sug-meta"><span className="sb-sug-country">{subtitle}</span></div>}
      </div>
    </button>
  );
}

// ── Date helpers ──────────────────────────────────────────────────────────────

const DAYS   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function fmtISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function fmtDisplay(d: Date): string {
  return `${DAYS[d.getDay()]}, ${String(d.getDate()).padStart(2,'0')} ${MONTHS[d.getMonth()]}`;
}

/**
 * Resolve the react-datepicker range-select result into a valid check-in/check-out pair.
 * Same-day is never allowed for both: clicking the check-in date again (react-datepicker
 * yields `end === start`, or a repeat `[start, null]` against the existing check-in)
 * auto-selects the NEXT day as check-out. Any `end <= start` is bumped to `start + 1 day`.
 * Pure + exported so the rule is unit-tested independently of react-datepicker.
 */
export function resolveDateRange(
  start: Date | null,
  end: Date | null,
  currentCheckin: Date | null,
): { checkin: Date | null; checkout: Date | null } {
  const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  if (start && ((end && end.getTime() <= start.getTime()) || (!end && currentCheckin && sameDay(start, currentCheckin)))) {
    const bumped = new Date(start);
    bumped.setDate(bumped.getDate() + 1);
    return { checkin: start, checkout: bumped };
  }
  return { checkin: start, checkout: end };
}

// ── Recent search localStorage (mirrors old CMS `searches` key, max 3) ────────

const RECENT_KEY = 'searches';
const RECENT_MAX = 3;

// Exact shape of old CMS localStorage['searches'] entries — no label stored
export interface RecentSearch {
  dest_id: string;
  checkin: string;
  checkout: string;
  rooms: number;
  adults: string;   // "2" or "2,3" per room
  children: string; // "1_5,1_10,2_7" or ""
  ratecode: string;
  // _label is a local display cache only, never sent in URL or API
  _label?: string;
}

export function loadRecent(): RecentSearch[] {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch { return []; }
}

function isSameSearch(a: RecentSearch, b: RecentSearch): boolean {
  return (
    a.dest_id   === b.dest_id   &&
    a.checkin   === b.checkin   &&
    a.checkout  === b.checkout  &&
    String(a.rooms) === String(b.rooms) &&
    a.adults    === b.adults    &&
    a.children  === b.children  &&
    a.ratecode  === b.ratecode
  );
}

function saveRecent(entry: RecentSearch) {
  const list = loadRecent();
  if (list.some((r) => isSameSearch(r, entry))) return;
  list.unshift(entry);
  localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
}

function removeRecent(entry: RecentSearch): RecentSearch[] {
  const list = loadRecent().filter((r) => !isSameSearch(r, entry));
  localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  return list;
}

// A recent entry can lack a human label — saved from the detail rate form (destination field
// hidden) or a legacy entry. Never surface the raw `dest_id` (a 24-hex Mongo ObjectId) as the
// title; fall back to a neutral label (the date row below still identifies the search).
const OBJECT_ID_RE = /^[a-f\d]{24}$/i;
/** The entry's own usable display label, or null when it's missing / a raw dest_id (legacy entries,
 *  or ones re-selected from a label-less search). Callers then resolve the name from `dest_id`. */
function validRecentLabel(r: RecentSearch): string | null {
  const l = (r._label || '').trim();
  return l && !OBJECT_ID_RE.test(l) ? l : null;
}

/** Persist resolved `dest_id → name` labels back into the `searches` store so subsequent loads show
 *  the name instantly (no re-fetch) — legacy CMS entries carried no `_label`. */
function backfillRecentLabels(map: Record<string, string>) {
  const list = loadRecent();
  let changed = false;
  for (const r of list) {
    if (map[r.dest_id] && !validRecentLabel(r)) { r._label = map[r.dest_id]; changed = true; }
  }
  if (changed) localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
}

// ── Validation ────────────────────────────────────────────────────────────────
// Reusable — can be called from any page that embeds SearchBar

export interface SearchValidationError {
  field: 'destination' | 'destId' | 'checkin' | 'checkout';
  message: string;
}

export function validateSearchPayload(params: {
  destination: string;
  destId: string;
  checkin: Date | null;
  checkout: Date | null;
}): SearchValidationError | null {
  if (!params.destination.trim()) return { field: 'destination', message: 'Please enter a destination' };
  if (!params.destId) return { field: 'destId', message: 'Please select a destination from the list' };
  if (!params.checkin) return { field: 'checkin', message: 'Please select a check-in date' };
  if (!params.checkout) return { field: 'checkout', message: 'Please select a check-out date' };
  return null;
}

// ── Props ─────────────────────────────────────────────────────────────────────

/**
 * Embed (white-label plugin) mode — additive, opt-in. Used ONLY by the booking-engine-landing
 * iframe; every field is optional so existing call sites (home / search / detail rate form) are
 * untouched. Submit still flows through `onSearch` (the landing posts `openUrl` to the parent).
 */
export interface SearchBarEmbedConfig {
  /** Freeze the destination field (plugin `fixLocation==1`): prefilled from `initialDestination`,
   *  disabled + read-only, no clear button, no suggestions. */
  freezeDestination?: boolean;
  /** Render the ratecode field as a `password` masked with the visual mask (plugin
   *  `maskAffiliateCode==1`). `initialRatecode` should be the mask ('X'×length); the first keystroke
   *  strips the mask. On submit the landing forwards `bip_k` (not the mask) when it's unchanged. */
  ratecodeMasked?: boolean;
  ratecodeMaskLength?: number;
  /** Custom search-button label (plugin `btnText`). Defaults to "Search". */
  buttonLabel?: string;
}

export interface SearchBarProps {
  initialDestination?: string;
  initialDestId?: string;          // MongoDB _id of SearchLocation (dest_id in URL)
  initialCheckin?: string;
  initialCheckout?: string;
  initialRooms?: Room[];
  initialRatecode?: string;
  /** Masked-ratecode carried in from a `bip_k`/`bip_l` URL (non-embed pages — search/detail).
   *  When both are set, the field renders masked and an UNCHANGED resubmit forwards `bip_k`/`bip_l`
   *  instead of the literal visual mask text. Distinct from `embed`, which is plugin-landing-only. */
  initialBipK?: string;
  initialBipL?: string;
  hideDestination?: boolean;       // detail-page rate form: dest is fixed from URL
  embed?: SearchBarEmbedConfig;    // white-label plugin landing only (additive)
  onSearch?: (params: Record<string, string>) => void;
  compact?: boolean;               // list page: tighter search button padding
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function SearchBar({
  initialDestination = '',
  initialDestId = '',
  initialCheckin,
  initialCheckout,
  initialRooms,
  initialRatecode = '',
  initialBipK,
  initialBipL,
  hideDestination = false,
  embed,
  onSearch,
  compact = false,
}: SearchBarProps) {
  const router = useRouter();
  const frozenDest = !!embed?.freezeDestination;
  const bipMaskLength = parseBipMaskLength(initialBipL);
  const ratecodeMasked = !!embed?.ratecodeMasked || (!!initialBipK && bipMaskLength > 0);
  const ratecodeMaskLength = embed?.ratecodeMaskLength ?? bipMaskLength;
  const defaults = useMemo(() => buildDefaultDates(), []);
  const hotelsClient = useMemo(() => new HotelsClient(HOTELS_API_BASE_URL), []);

  // ── Form state ──
  const [destination, setDestination] = useState(initialDestination);
  const [destId, setDestId]           = useState(initialDestId);       // MongoDB _id
  const [checkin, setCheckin]   = useState<Date | null>(
    () => new Date((initialCheckin  || defaults.checkin)  + 'T00:00:00')
  );
  const [checkout, setCheckout] = useState<Date | null>(
    () => new Date((initialCheckout || defaults.checkout) + 'T00:00:00')
  );
  const [rooms, setRooms]     = useState<Room[]>(
    initialRooms ?? [{ adults: 2, children: 0, childAges: [] }]
  );
  const [ratecode, setRatecode] = useState(initialRatecode);
  const [error, setError]       = useState('');

  // ── Panel state (only one open at a time) ──
  const [showDatePanel, setShowDatePanel] = useState(false);
  const [occOpen, setOccOpen]             = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // ── Suggestion state ──
  const [suggestions, setSuggestions]               = useState<HotelSuggestion[]>([]);      // Mongo (flat) — live default
  const [groupedSuggestions, setGroupedSuggestions] = useState<Suggestions>({ citySuggestions: [], hotelSuggestions: [] }); // ES (grouped) — parallel
  const [recentSearches, setRecentSearches]         = useState<RecentSearch[]>([]);
  // dest_id → resolved destination name, for recent entries whose `_label` is missing/legacy.
  const [resolvedLabels, setResolvedLabels]         = useState<Record<string, string>>({});
  const labelFetchedRef                             = useRef<Set<string>>(new Set());
  // Selected destination's location fields, captured on suggestion/recent select, so a hotels
  // search can seed `theatre-searches-city` (cross-domain: the sports/shows landing reads the
  // last searched city from there). Sports reads `.city`; theaters read `.city` + `.id`.
  const selectedLocRef = useRef<{ city: string; state: string; country: string; id: string; dest_id: string } | null>(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  // Active suggestion engine. Starts at the build-time default (SSR-safe) and picks up a
  // `?engine=es`/`?engine=mongo` URL override on mount (window read only in an effect — React 19).
  const [engine, setEngine] = useState<SuggestionEngine>(SUGGESTION_ENGINE_DEFAULT);
  useEffect(() => {
    setEngine(resolveSuggestionEngine(new URLSearchParams(window.location.search).get('engine')));
  }, []);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef    = useRef<HTMLInputElement>(null);
  const wrapRef     = useRef<HTMLDivElement>(null);
  // Embed masked-ratecode: the field is prefilled with the visual mask ('X'×len); the FIRST edit
  // strips the mask so the user's typed code replaces it (mirrors CMS bookingEngineLanding.js).
  const ratecodeMaskedDotsRef = useRef(ratecodeMasked && ratecodeMaskLength > 0);

  function handleRatecodeChange(e: React.ChangeEvent<HTMLInputElement>) {
    let val = e.target.value;
    if (ratecodeMasked && ratecodeMaskedDotsRef.current) {
      val = val.replace(/X/g, '');
      ratecodeMaskedDotsRef.current = false;
    }
    setRatecode(val);
  }

  // ── Panel helpers (mutually exclusive) ────────────────────────────────────

  function openDates() {
    setOccOpen(false);
    setShowSuggestions(false);
    setShowDatePanel(true);
  }

  function handleOccOpenChange(val: boolean) {
    if (val) { setShowDatePanel(false); setShowSuggestions(false); }
    setOccOpen(val);
  }

  function openSuggestionsPanel() {
    setShowDatePanel(false);
    setOccOpen(false);
    setShowSuggestions(true);
  }

  function closeAll() {
    setShowDatePanel(false);
    setOccOpen(false);
    setShowSuggestions(false);
  }

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) closeAll();
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // ── Suggestions fetch ─────────────────────────────────────────────────────

  const fetchSuggestions = useCallback(async (q: string) => {
    if (q.trim().length < 2) {
      setSuggestions([]);
      setGroupedSuggestions({ citySuggestions: [], hotelSuggestions: [] });
      return;
    }
    setLoadingSuggestions(true);
    try {
      if (engine === 'es') {
        // ES: grouped top-3 cities + top-5 hotels (backend fixes the counts).
        const grouped = await hotelsClient.getSuggestionsEs(q.trim(), 8);
        setGroupedSuggestions(grouped);
      } else {
        const results = await hotelsClient.getSuggestions(q.trim(), 8);
        setSuggestions(results);
      }
    } catch {
      setSuggestions([]);
      setGroupedSuggestions({ citySuggestions: [], hotelSuggestions: [] });
    }
    finally { setLoadingSuggestions(false); }
  }, [hotelsClient, engine]);

  function handleDestinationChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    setDestination(val);
    setDestId('');           // clear until a suggestion is selected
    setError('');
    openSuggestionsPanel();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 350);
  }

  function handleDestinationFocus() {
    setRecentSearches(loadRecent());
    openSuggestionsPanel();
    if (destination.trim().length >= 2) fetchSuggestions(destination);
  }

  function clearDestination() {
    setDestination('');
    setDestId('');
    setSuggestions([]);
    setGroupedSuggestions({ citySuggestions: [], hotelSuggestions: [] });
    setRecentSearches(loadRecent());
    openSuggestionsPanel();
    inputRef.current?.focus();
  }

  // Resolve missing recent-search labels from their `dest_id` — legacy CMS entries (and ones
  // re-selected from a label-less search) carry no `_label`, so they'd show "Recent search" instead
  // of the destination name. Fetch each id once, cache in state, and backfill the store for next time.
  useEffect(() => {
    const ids = recentSearches
      .filter((r) => r.dest_id && !validRecentLabel(r) && !labelFetchedRef.current.has(r.dest_id))
      .map((r) => r.dest_id);
    if (ids.length === 0) return;
    ids.forEach((id) => labelFetchedRef.current.add(id));
    let cancelled = false;
    Promise.all(
      ids.map((id) =>
        hotelsClient.getLocationLabel(id).then((l) => [id, l] as const).catch(() => [id, null] as const),
      ),
    ).then((pairs) => {
      if (cancelled) return;
      const map: Record<string, string> = {};
      for (const [id, l] of pairs) if (l && !OBJECT_ID_RE.test(l)) map[id] = l;
      if (Object.keys(map).length > 0) {
        setResolvedLabels((prev) => ({ ...prev, ...map }));
        backfillRecentLabels(map);
      }
    });
    return () => { cancelled = true; };
  }, [recentSearches, hotelsClient]);

  // The destination name shown for a recent entry: its own label, else the resolved one, else neutral.
  const displayRecentLabel = (r: RecentSearch): string =>
    validRecentLabel(r) ?? resolvedLabels[r.dest_id] ?? 'Recent search';

  function handleSuggestionSelect(s: HotelSuggestion) {
    setDestination(s.label);
    setDestId(s.id);   // s.id = SearchLocation _id (dest_id). For a property suggestion this
    // Capture location fields for the `theatre-searches-city` seed on submit (see handleSubmit).
    selectedLocRef.current = {
      city:    s.cityName || s.label1 || (s.label || '').split(',')[0].trim(),
      state:   s.stateName || '',
      country: s.country || '',
      id:      s.destinationId || '', // theaters use this as cityId
      dest_id: s.id || '',
    };
                       // is the property's own SearchLocation record — the backend resolves
                       // its city (destination_id) and pins the property as a sponsored/
                       // affiliate-style card in the city results, mirroring the CMS.
    setError('');
    closeAll();
    setTimeout(openDates, 80);   // auto-open calendar after destination chosen
  }

  function handleRecentSelect(r: RecentSearch) {
    const { checkin: ci, checkout: co } = bumpPastDates(r.checkin, r.checkout);
    setDestination(r._label ?? '');
    setDestId(r.dest_id);
    // Recent entries carry no separate location fields — derive city/state/country from the
    // `_label` ("City, State, Country") so the theatre seed still gets the city (no destinationId).
    const lp = (r._label || '').split(',').map((x) => x.trim());
    selectedLocRef.current = { city: lp[0] || '', state: lp[1] || '', country: lp[2] || '', id: '', dest_id: r.dest_id };
    setCheckin(new Date(ci + 'T00:00:00'));
    setCheckout(new Date(co + 'T00:00:00'));
    setRooms(parseOccupancyCMS(r.rooms, r.adults, r.children));
    setRatecode(r.ratecode || '');
    setError('');
    closeAll();
  }

  // ── Submit ────────────────────────────────────────────────────────────────

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    closeAll();

    const validationErr = hideDestination
      ? (!checkin ? { field: 'checkin' as const, message: 'Please select a check-in date' } : !checkout ? { field: 'checkout' as const, message: 'Please select a check-out date' } : null)
      : validateSearchPayload({ destination, destId, checkin, checkout });
    if (validationErr) {
      setError(validationErr.message);
      if (validationErr.field === 'destination' || validationErr.field === 'destId') {
        inputRef.current?.focus();
      }
      return;
    }

    const occ = serializeOccupancyCMS(rooms);
    const trimmedRatecode = ratecode.trim();
    // Masked-ratecode resubmit: if the field is masked and still shows the untouched visual mask,
    // forward the real `bip_k`/`bip_l` instead of the literal mask text (which would otherwise
    // silently replace the real affiliate code with e.g. "XXXX"). Only applies when the caller gave
    // us the token via `initialBipK` — the embed page (booking-engine-landing) never does, since it
    // does this same correction itself in its own `onSearch` callback before forwarding.
    const visualMask = ratecodeMasked && ratecodeMaskLength > 0 ? 'X'.repeat(ratecodeMaskLength) : '';
    const ratecodeUnchanged = ratecodeMasked && !!initialBipK && trimmedRatecode === visualMask;

    const params: Record<string, string> = {
      dest_id:  destId,
      checkin:  fmtISO(checkin!),
      checkout: fmtISO(checkout!),
      rooms:    String(occ.rooms),
      adults:   occ.adults,
      children: occ.children,
      ...(ratecodeUnchanged
        ? { bip_k: initialBipK!, ...(ratecodeMaskLength ? { bip_l: String(ratecodeMaskLength) } : {}) }
        : trimmedRatecode ? { ratecode: trimmedRatecode } : {}),
    };

    if (destId) {
      const entry: RecentSearch = {
        dest_id:  destId,
        checkin:  fmtISO(checkin!),
        checkout: fmtISO(checkout!),
        rooms:    occ.rooms,
        adults:   occ.adults,
        children: occ.children,
        // Never persist the literal visual mask text into recent searches — it isn't a real code.
        ratecode: ratecodeUnchanged ? '' : ratecode.trim(),
        // Capture the best available label — `destination` is empty on the detail rate form
        // (field hidden), so fall back to the page's initialDestination so the recent entry
        // still shows a name instead of the raw dest_id.
        _label:   destination || initialDestination || '',
      };
      saveRecent(entry);
      // Dual-write to bip_hotel_recent_searches (home-page section reads from this key).
      // Once we pick a winner between the two stores, drop one of the two write sites.
      addRecentSearch(entry);

      // Cross-domain seed: write the chosen city into `theatre-searches-city` so the sports/shows
      // landing picks it up (mirrors what sports/CMS do the other way). Use the captured location
      // fields when they match this search; otherwise fall back to parsing the destination label.
      const loc =
        selectedLocRef.current && selectedLocRef.current.dest_id === destId
          ? selectedLocRef.current
          : (() => {
              const p = (destination || initialDestination || '').split(',').map((x) => x.trim());
              return { city: p[0] || '', state: p[1] || '', country: p[2] || '', id: '', dest_id: destId };
            })();
      if (loc.city) {
        try {
          localStorage.setItem(
            'theatre-searches-city',
            JSON.stringify({ city: loc.city, state: loc.state, country: loc.country, id: loc.id, dest_id: loc.dest_id }),
          );
        } catch {
          /* localStorage disabled / quota — non-fatal */
        }
      }
    }

    if (onSearch) {
      onSearch(params);
    } else {
      const targetQs = buildQueryString(params);
      const alreadyOnPage =
        typeof window !== 'undefined' &&
        window.location.pathname === `${PROXY_PREFIX}/hotels/search` &&
        window.location.search === `?${targetQs}`;
      if (alreadyOnPage) {
        // Same URL — router.push is a no-op; re-trigger search directly
        window.dispatchEvent(new CustomEvent('hotelsFiltersChanged', { detail: {} }));
      } else {
        router.push(`${PROXY_PREFIX}/hotels/search?${targetQs}`);
      }
    }
  }

  // ── Derived display ───────────────────────────────────────────────────────

  const nights = checkin && checkout ? nightsBetween(fmtISO(checkin), fmtISO(checkout)) : 0;

  const datesLabel = checkin && checkout
    ? `${fmtDisplay(checkin)} – ${fmtDisplay(checkout)}`
    : checkin
      ? `${fmtDisplay(checkin)} – Check-out`
      : 'Add dates';

  const showRecentPanel = showSuggestions && destination.trim().length < 2 && recentSearches.length > 0;
  const showSugPanel    = showSuggestions && destination.trim().length >= 2;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="sb-wrap" ref={wrapRef}>
      <form onSubmit={handleSubmit} noValidate>
        <div className={`sb-bar${hideDestination ? ' sb-bar--no-dest' : ''}`}>

          {/* ── Destination (hidden on detail-page rate form) ── */}
          {!hideDestination && (
            <div className={`sb-field sb-field--dest${showSuggestions ? ' sb-field--active' : ''}`}>
              <span className="sb-field__label sr-only">Destination</span>
              <div className="sb-field__body">
                <i className="fa-light fa-magnifying-glass sb-field__icon" aria-hidden="true" />
                <input
                  ref={inputRef}
                  type="text"
                  className="sb-dest-input"
                  placeholder="Where are you going?"
                  value={destination}
                  onChange={frozenDest ? undefined : handleDestinationChange}
                  onFocus={frozenDest ? undefined : handleDestinationFocus}
                  disabled={frozenDest}
                  readOnly={frozenDest}
                  // Chrome IGNORES autoComplete="off" for fields it heuristically reads as a name/
                  // address (the "Amit Gupta / Manage addresses…" profile popup over this box). A
                  // non-credential token like "new-password" reliably suppresses that profile autofill
                  // on a text input (no key icon / save-password prompt — that's type=password only);
                  // the data-*-ignore attrs do the same for 1Password / LastPass. Our own suggestion
                  // listbox is unaffected.
                  autoComplete="new-password"
                  data-1p-ignore
                  data-lpignore="true"
                  aria-label="Destination"
                  aria-autocomplete="list"
                  aria-expanded={showSuggestions}
                />
                {destination && !frozenDest && (
                  <button type="button" className="sb-clear-btn" onClick={clearDestination} aria-label="Clear destination">×</button>
                )}
              </div>

              {/* Suggestions are anchored to the destination FIELD (not the whole bar) so they drop
                  directly below it — including the stacked/mobile layout where the bar is tall. */}
              {!frozenDest && showRecentPanel && (
                <div className="sb-suggestions" role="listbox" aria-label="Recent searches">
                  <div className="sb-sug-section-title">Recent searches</div>
                  {recentSearches.map((r, idx) => {
                    const { checkin: ci, checkout: co } = bumpPastDates(r.checkin, r.checkout);
                    const ciDate = new Date(ci + 'T00:00:00');
                    const coDate = new Date(co + 'T00:00:00');
                    const n = nightsBetween(ci, co);
                    const occ = summarizeOccupancy(parseOccupancyCMS(r.rooms, r.adults, r.children));
                    return (
                      <div key={idx} className="sb-recent-item" role="option">
                        <button type="button" className="sb-recent-item__main" onClick={() => handleRecentSelect(r)}>
                          <div className="sb-sug-icon sb-sug-icon--recent" aria-hidden="true">
                            <i className="fas fa-history" />
                          </div>
                          <div className="sb-sug-text">
                            <div className="sb-sug-name">{displayRecentLabel(r)}</div>
                            {/* Stacked rows (date / nights · guests) — a single flex row shrank each
                                piece and wrapped mid-text, which read as scattered. */}
                            <div className="sb-recent-meta">{fmtDisplay(ciDate)} – {fmtDisplay(coDate)}</div>
                            <div className="sb-recent-meta">
                              {n} night{n !== 1 ? 's' : ''} · {occ}{r.ratecode ? ` · ${r.ratecode}` : ''}
                            </div>
                          </div>
                        </button>
                        <button
                          type="button"
                          className="sb-recent-item__delete"
                          aria-label="Remove this search"
                          onClick={(e) => { e.stopPropagation(); setRecentSearches(removeRecent(r)); }}
                        >
                          <i className="fas fa-times" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {!frozenDest && showSugPanel && (
                <div className="sb-suggestions" role="listbox" aria-label="Destination suggestions">
                  {loadingSuggestions ? (
                    <div className="sb-sug-loading">
                      <i className="fas fa-circle-notch fa-spin" aria-hidden="true" />
                      Searching destinations…
                    </div>
                  ) : engine === 'es' ? (
                    // ── Elasticsearch grouped dropdown: CITIES (top 3) + HOTELS (top 5) ──
                    (!groupedSuggestions.citySuggestions.length && !groupedSuggestions.hotelSuggestions.length) ? (
                      <div className="sb-sug-empty">No results found</div>
                    ) : (
                      <>
                        {groupedSuggestions.citySuggestions.length > 0 && (
                          <>
                            <div className="sb-sug-group-label">CITIES</div>
                            {groupedSuggestions.citySuggestions.map((s) => {
                              const cfg = TYPE_CONFIG[s.type || ''] ?? { icon: 'fa-map-marker-alt', cls: 'default' };
                              return (
                                <SuggestionRow
                                  key={s.id}
                                  cfg={cfg}
                                  title={s.cityName || s.label}
                                  subtitle={[s.stateName, s.country].filter(Boolean).join(', ')}
                                  query={destination}
                                  onClick={() => handleSuggestionSelect(s)}
                                />
                              );
                            })}
                          </>
                        )}
                        {groupedSuggestions.hotelSuggestions.length > 0 && (
                          <>
                            <div className={`sb-sug-group-label${groupedSuggestions.citySuggestions.length ? ' sb-sug-group-label--divider' : ''}`}>HOTELS</div>
                            {groupedSuggestions.hotelSuggestions.map((s) => {
                              const cfg = TYPE_CONFIG[s.type || ''] ?? { icon: 'fa-map-marker-alt', cls: 'default' };
                              return (
                                <SuggestionRow
                                  key={s.id}
                                  cfg={cfg}
                                  title={s.label1 || s.label}
                                  subtitle={[s.cityName, s.country].filter(Boolean).join(', ')}
                                  query={destination}
                                  onClick={() => handleSuggestionSelect(s)}
                                />
                              );
                            })}
                          </>
                        )}
                      </>
                    )
                  ) : suggestions.length === 0 ? (
                    <div className="sb-sug-empty">No results found</div>
                  ) : (
                    suggestions.map((s) => {
                      const cfg = TYPE_CONFIG[s.type || ''] ?? { icon: 'fa-map-marker-alt', cls: 'default' };
                      return (
                        <button key={s.id} type="button" role="option" className="sb-sug-item" onClick={() => handleSuggestionSelect(s)}>
                          <div className={`sb-sug-icon sb-sug-icon--${cfg.cls}`} aria-hidden="true">
                            <i className={`fas ${cfg.icon}`} />
                          </div>
                          <div className="sb-sug-text">
                            <div className="sb-sug-name">{s.label}</div>
                            <div className="sb-sug-meta">
                              <span className={`sb-sug-badge sb-sug-badge--${cfg.cls}`}>{s.type || 'place'}</span>
                              {s.country && <span className="sb-sug-country">{s.country}</span>}
                            </div>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Dates (single unified field) ── */}
          <div
            className={`sb-field sb-field--dates${showDatePanel ? ' sb-field--active' : ''}`}
            onClick={openDates}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && openDates()}
            aria-label="Select dates"
            aria-haspopup="true"
            aria-expanded={showDatePanel}
          >
            <span className="sb-field__label sr-only">Dates</span>
            <div className="sb-field__body">
              <i className="fa-light fa-calendar-range sb-field__icon" aria-hidden="true" />
              <span className={(checkin && checkout) ? 'sb-date-value' : 'sb-date-value sb-date-value--empty'}>
                {datesLabel}
              </span>
            </div>

            {/* Calendar anchored to the Dates FIELD (matches the occupancy/suggestions pattern) so it
                drops directly below it on every layout. stopPropagation keeps panel clicks (incl. Done)
                from bubbling to the field's open handler. */}
            {showDatePanel && (
              <div className="sb-date-panel" onClick={(e) => e.stopPropagation()}>
                <DatePicker
                  selected={checkin}
                  onChange={(dates) => {
                    const [start, end] = dates as [Date | null, Date | null];
                    const resolved = resolveDateRange(start, end, checkin);
                    setCheckin(resolved.checkin);
                    setCheckout(resolved.checkout);
                    if (resolved.checkin && resolved.checkout) setShowDatePanel(false);
                  }}
                  startDate={checkin}
                  endDate={checkout}
                  selectsRange
                  inline
                  monthsShown={2}
                  minDate={new Date()}
                />
                <div className="sb-date-panel__footer">
                  <span className="sb-date-panel__hint">
                    {!checkin ? 'Select check-in date' : !checkout ? 'Select check-out date' : `${nights} night${nights !== 1 ? 's' : ''} selected`}
                  </span>
                  <button type="button" onClick={() => setShowDatePanel(false)} className="sb-date-panel__done">Done</button>
                </div>
              </div>
            )}
          </div>

          {/* ── Guests ── */}
          <div className={`sb-field sb-field--guests${occOpen ? ' sb-field--active' : ''}`}>
            <span className="sb-field__label sr-only">Guests &amp; Rooms</span>
            <OccupancyPicker
              value={rooms}
              onChange={setRooms}
              open={occOpen}
              onOpenChange={handleOccOpenChange}
            />
          </div>

          {/* ── Special / promo code (ratecode) ── */}
          <div className="sb-field sb-field--code sb-field--focusable">
            <span className="sb-field__label sr-only">Special Code</span>
            <div className="sb-field__body">
              <i className="fa-sharp fa-light fa-sparkle sb-field__icon" aria-hidden="true" />
              <input
                // CMS parity: the masked value is always the literal 'X'-repeated placeholder
                // ('XXXX'), never the real code (that lives in bip_k/bip_l, never in this field) —
                // so there's nothing to actually hide. `type="password"` used to render those X's as
                // dots instead of the literal characters CMS shows; plain text matches CMS exactly.
                type="text"
                className="sb-code-input"
                placeholder="Special Code"
                value={ratecode}
                onChange={handleRatecodeChange}
                aria-label="Special / promo code"
                autoComplete="off"
              />
            </div>
          </div>

          {/* ── Search ── */}
          <div className="flex items-center justify-center p-2">
            <button
              type="submit"
              className="sb-search-btn"
              style={compact ? { paddingTop: '0.65rem', paddingBottom: '0.65rem' } : undefined}
            >
              <span>{embed?.buttonLabel || 'Search'}</span>
            </button>
          </div>
        </div>

        {error && <p className="mt-2 text-sm text-red px-2" role="alert">{error}</p>}
      </form>
    </div>
  );
}
